// ============================================
// APP: NVA (React App)
// FILE: src/pages/Home.tsx
// PURPOSE: La Home. Decide QUALE Home mostrare e monta quello che serve a
//          tutte; le viste compongono la pagina.
//
// Quattro viste, da lib/homeState (le date della prenotazione e l'ora di
// Roma, ricalcolate a ogni minuto dal clock):
//   - G, ospite non entrato → HomeGuest (con il pulsante flottante
//     dell'Assistente AI: solo qui; dentro, la chat sta in "Reception 24h");
//   - B, prima dell'arrivo e il giorno dell'arrivo fino alle 15:00 →
//     HomeBefore;
//   - S, soggiorno, e A, il giorno dopo la partenza → HomeStay.
//
// Qui vivono anche:
//   - la sessione aggiornata: refreshSession all'apertura, a ogni cambio di
//     token (chat/auth non manda indirizzo, extra comprati, tassa del
//     gruppo) e tornando sull'app, al massimo una volta al minuto;
//   - il catalogo degli extra (orario di uscita vero, taxi già preso);
//   - l'intestazione espresso sotto la barra di stato (lib/statusBar);
//   - 5 tocchi veloci sullo stemma: il pannello di prova della barra di
//     stato, solo per il proprietario (statusBarProbe);
//   - HomeSessionHost (login, chat, assistente, la scelta AI/reception) e
//     ServiceDialogsHost (i servizi a tutta pagina e i vecchi link #bike...);
//   - lo scorrimento: il contenitore resta "tab:home" (si ritrova il posto
//     tornando), ma cambiando vista si riparte dall'alto: una posizione
//     salvata nella vista ospite finirebbe a metà della vista soggiorno.
// ============================================

import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import FloatingChatButton from "../components/FloatingChatButton";
import ServiceDialogsHost from "../components/home/services/ServiceDialogsHost";
import HomeSessionHost, { useHomeSession } from "../components/home/HomeSessionHost";
import HomeGuest from "../components/home/HomeGuest";
import HomeBefore from "../components/home/HomeBefore";
import HomeStay from "../components/home/HomeStay";
import { useGuestSession } from "../hooks/useGuestSession";
import { useExtrasPreview } from "../hooks/useExtrasPreview";
import { useNow } from "../hooks/useNow";
import { forgetScroll } from "../hooks/useScrollRestoration";
import { deriveHomeState, type HomeView } from "../lib/homeState";
import { HOME_COLOR, useStatusBarColor } from "../lib/statusBar";
import { useStatusBarProbe } from "../components/statusBarProbe/ProbeTrigger";

const SCROLL_KEY = "tab:home";
/** Tornando sull'app la prenotazione si rilegge, ma non più di una volta al minuto. */
const REFRESH_GAP_MS = 60 * 1000;

// L'ultima vista mostrata, anche fra un'apertura e l'altra della Home: se
// al ritorno la vista è cambiata, la posizione salvata non vale più.
let lastView: HomeView | null = null;

/** Il pulsante flottante (solo vista G) con il conteggio dei non letti dell'host. */
const GuestFloatingChat: React.FC = () => {
  const { unreadCount } = useHomeSession();
  return <FloatingChatButton unreadCount={unreadCount} />;
};

const Home: React.FC = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || "it";
  const { isLoggedIn, token, booking, guestName, refreshSession } = useGuestSession();
  const now = useNow();
  const catalog = useExtrasPreview(isLoggedIn ? token : null, lang);
  const scrollRef = useRef<HTMLDivElement>(null);

  const state = useMemo(
    () => deriveHomeState(isLoggedIn && booking ? { booking, catalog } : null, now),
    [isLoggedIn, booking, catalog, now],
  );

  useStatusBarColor(HOME_COLOR);
  const probe = useStatusBarProbe();

  // La prenotazione fresca: all'apertura e a ogni token nuovo (login, push,
  // collegamento automatico), poi tornando sull'app.
  const lastRefresh = useRef(0);
  useEffect(() => {
    if (!token) return undefined;
    lastRefresh.current = Date.now();
    void refreshSession();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefresh.current < REFRESH_GAP_MS) return;
      lastRefresh.current = Date.now();
      void refreshSession();
    };
    document.addEventListener("visibilitychange", onVisible);
    // "Il check-in va rifatto" (push in primo piano): si rilegge subito.
    const onCheckInUpdate = () => {
      lastRefresh.current = Date.now();
      void refreshSession();
    };
    window.addEventListener("nva_checkin_update", onCheckInUpdate);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("nva_checkin_update", onCheckInUpdate);
    };
  }, [token, refreshSession]);

  // Riaprendo la Home su una vista diversa dall'ultima, si parte dall'alto.
  // Gira prima del ScrollRestorer del Layout (che sta dopo la pagina).
  useLayoutEffect(() => {
    if (lastView !== null && lastView !== state.view) forgetScroll(SCROLL_KEY);
    lastView = state.view;
    // Solo all'apertura: i cambi di vista con la Home aperta sono sotto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambio di vista con la Home aperta (login, uscita, le 15:00 del giorno
  // dell'arrivo, la mezzanotte dopo la partenza): di nuovo dall'alto.
  const shownView = useRef(state.view);
  useEffect(() => {
    lastView = state.view;
    if (shownView.current === state.view) return;
    shownView.current = state.view;
    forgetScroll(SCROLL_KEY);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [state.view]);

  let view: React.ReactNode;
  if (state.view === "guest" || !token || !booking) {
    view = <HomeGuest state={state} now={now} />;
  } else if (state.view === "before") {
    view = <HomeBefore state={state} now={now} token={token} booking={booking} guestName={guestName} catalog={catalog} />;
  } else {
    view = <HomeStay state={state} now={now} token={token} booking={booking} guestName={guestName} catalog={catalog} />;
  }

  return (
    <div
      ref={scrollRef}
      data-scroll-root
      data-scroll-key={SCROLL_KEY}
      onClick={probe.onClick}
      className="absolute inset-0 overflow-y-auto overflow-x-hidden bg-nva-bone dark:bg-stone-950"
      style={{
        bottom: "calc(72px + env(safe-area-inset-bottom))",
        top: 0,
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        touchAction: "pan-y",
      }}
    >
      <ServiceDialogsHost>
        <HomeSessionHost>
          <div className="flex min-h-full w-full flex-col pb-8">
            {view}
          </div>
          {state.view === "guest" ? <GuestFloatingChat /> : null}
        </HomeSessionHost>
      </ServiceDialogsHost>
      {probe.panel}
    </div>
  );
};

export default Home;
